import { useEffect } from 'react';
import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app';
import { useNavigate } from 'react-router-dom';

/**
 * Hook pour gérer les deep links (nazandoo://) et app links (https://)
 * 
 * @example
 * // Dans App.tsx
 * import { useDeepLinking } from './hooks/useDeepLinking';
 * 
 * function App() {
 *   useDeepLinking();
 *   return <Router>...</Router>;
 * }
 */
export const useDeepLinking = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Écouter les événements d'ouverture d'URL
    const listener = CapacitorApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const url = event.url;
      console.log('🔗 Deep link reçu:', url);

      try {
        const path = extractPathFromUrl(url);
        
        if (path) {
          console.log('📍 Navigation vers:', path);
          
          // Délai court pour s'assurer que l'app est prête
          setTimeout(() => {
            navigate(path);
          }, 100);
        } else {
          console.warn('⚠️ Impossible d\'extraire le chemin de:', url);
        }
      } catch (error) {
        console.error('❌ Erreur lors du traitement du deep link:', error);
      }
    });

    // Vérifier si l'app a été ouverte via un deep link
    CapacitorApp.getLaunchUrl().then((result) => {
      if (result?.url) {
        console.log('🚀 App lancée via deep link:', result.url);
        const path = extractPathFromUrl(result.url);
        if (path) {
          setTimeout(() => {
            navigate(path);
          }, 500); // Délai plus long au lancement
        }
      }
    });

    // Cleanup
    return () => {
      listener.then(l => l.remove());
    };
  }, [navigate]);
};

/**
 * Extraire le chemin de navigation depuis une URL
 * 
 * @param url - URL complète (nazandoo://... ou https://...)
 * @returns Chemin pour React Router (/product/123)
 */
function extractPathFromUrl(url: string): string {
  try {
    // Custom scheme (nazandoo://product/123)
    if (url.startsWith('nazandoo://')) {
      const path = url.replace('nazandoo://', '');
      return `/${path}`;
    }

    // HTTPS URLs (https://nazandoo.com/product/123)
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlObj = new URL(url);
      
      // Vérifier que c'est un domaine autorisé
      const allowedHosts = ['nazandoo.com', 'www.nazandoo.com', 'app.nazandoo.com'];
      if (!allowedHosts.includes(urlObj.hostname)) {
        console.warn('⚠️ Domaine non autorisé:', urlObj.hostname);
        return '';
      }

      // Retourner le chemin + query params
      return urlObj.pathname + urlObj.search;
    }

    return '';
  } catch (error) {
    console.error('❌ Erreur lors de l\'extraction du chemin:', error);
    return '';
  }
}

/**
 * Valider si une URL est autorisée pour deep linking
 * 
 * @param url - URL à valider
 * @returns true si l'URL est sûre
 */
export function validateDeepLinkUrl(url: string): boolean {
  try {
    const ALLOWED_SCHEMES = ['nazandoo', 'http', 'https'];
    const ALLOWED_HOSTS = ['nazandoo.com', 'www.nazandoo.com', 'app.nazandoo.com'];

    // Custom scheme
    if (url.startsWith('nazandoo://')) {
      return true;
    }

    // HTTP/HTTPS
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlObj = new URL(url);
      const scheme = urlObj.protocol.replace(':', '');
      
      return (
        ALLOWED_SCHEMES.includes(scheme) &&
        ALLOWED_HOSTS.includes(urlObj.hostname)
      );
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Générer une URL de deep link pour partage
 * 
 * @param path - Chemin dans l'app (/product/123)
 * @param useHttps - Utiliser HTTPS au lieu du custom scheme
 * @returns URL complète pour partage
 * 
 * @example
 * generateDeepLink('/product/123', true)  // https://nazandoo.com/product/123
 * generateDeepLink('/product/123', false) // nazandoo://product/123
 */
export function generateDeepLink(path: string, useHttps = true): string {
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  
  if (useHttps) {
    return `https://nazandoo.com/${cleanPath}`;
  } else {
    return `nazandoo://${cleanPath}`;
  }
}
